import { useState, useEffect } from 'react';
import { Inquiry, InquiryStatus, FormResponse } from '@/types/crm';
import { Mail, Trash2, XCircle, Zap, Calendar, MapPin, Package, MessageSquare, Megaphone, FileText, Link, Clock, Save, ArrowRight, ExternalLink, ClipboardList, AlertTriangle, RefreshCw } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle } from
'@/components/ui/dialog';

// UX-2 — the stage↔status mappings live in the service next to the
// enum tables they mirror. The modal only ever shows Status; stage is
// derived invisibly so the projects side stays consistent.
import { useUpdateInquiry, useConvertInquiryToProject, useCreateInquiry, stageToInquiryStatus, statusToStage } from '@/services/inquiries';
import { useOrganization } from '@/services/organizations';
import { useToast } from '@/hooks/use-toast';
// HOTFIX-4 — "Organization not found" toasts carry a Set up action.
import { ToastAction } from '@/components/ui/toast';
import { useNavigate } from 'react-router-dom';
import { NovaSpinner } from '@/components/nova';
import debug from '@/lib/debug';

/**
 * HOTFIX-2 — Supabase hands back a `PostgrestError`, which is a PLAIN
 * OBJECT, not an `Error` instance. Every `error instanceof Error &&
 * error.message` check in this file was therefore false for the exact
 * failures we most need to read, and the studio got a bare "Failed
 * to..." with the reason thrown away. `debug.parseSupabaseError`
 * handles both shapes (and translates the common Postgres codes into
 * something a photographer can act on), so it is the only thing that
 * should ever build one of these toast descriptions.
 */
const errorText = (error: unknown): string => debug.parseSupabaseError(error);

interface InquiryModalProps {
  inquiry: Inquiry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate: (inquiry: Inquiry) => void;
  onDelete: (id: string) => void;
}

// UX-2 — Status is the ONE pipeline a lead shows. The old Project
// Stage dropdown (10 project_stage values) rendered right next to
// this and gave every lead two parallel state machines — it confused
// users and the codebase alike (two shipped enum mixups started
// here). Stage still exists as a PROJECT concept (the /projects
// kanban, iOS) and is still synced invisibly; see handleStatusChange.
const statusOptions: {value: InquiryStatus;label: string;}[] = [
{ value: 'pending', label: 'Pending' },
{ value: 'accepted', label: 'Accepted' },
{ value: 'in_progress', label: 'In Progress' },
{ value: 'follow_up', label: 'Follow Up' },
{ value: 'completed', label: 'Completed' },
{ value: 'closed_lost', label: 'Closed Lost' }];

// Inner component that only renders when inquiry exists
const InquiryModalContent = ({ inquiry, open, onOpenChange, onUpdate, onDelete }: InquiryModalProps & {inquiry: Inquiry;}) => {
  const updateInquiry = useUpdateInquiry();
  const createInquiry = useCreateInquiry();
  const convertToProject = useConvertInquiryToProject();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: organization } = useOrganization();
  const isClientDerived = inquiry.id.startsWith('client-');

  // Initialize formData with defaults to prevent undefined values
  const [formData, setFormData] = useState<Inquiry>(() => ({
    ...inquiry,
    status: inquiry.status || 'pending',
    projectStage: inquiry.projectStage || 'lead'
  }));

  // Update formData when inquiry changes
  useEffect(() => {
    if (inquiry.id !== formData.id) {
      setFormData({
        ...inquiry,
        status: inquiry.status || 'pending',
        projectStage: inquiry.projectStage || 'lead'
      });
    }
  }, [inquiry.id]);

  const handleSave = async () => {
    if (isClientDerived) {
      // Client-derived entry: create a real inquiry record so we can persist stage/status
      try {
        const orgId = organization?.id;
        if (!orgId) {
          toast({
            title: 'Error',
            description: 'Your studio isn’t set up yet.',
            variant: 'destructive',
            // HOTFIX-4 — don't dead-end: take them to setup.
            action: (
              <ToastAction
                altText="Complete your studio setup"
                onClick={() => window.location.assign('/onboarding')}
              >
                Set up
              </ToastAction>
            ),
          });
          return;
        }
        const newInquiry = await createInquiry.mutateAsync({
          org_id: orgId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          status: formData.status,
          stage: formData.projectStage,
          service_type: formData.serviceType || 'general',
          description: formData.projectDescription || null,
          notes: formData.additionalNotes || null,
          location: formData.location || null,
          package: formData.packageSelected || null,
          how_heard: formData.heardAboutUs || null,
          portal_enabled: formData.clientPortalEnabled,
          estimated_completion: formData.estimatedCompletion || null,
        });
        toast({ title: 'Inquiry created', description: 'Client promoted to inquiry and changes saved.' });
        onUpdate(formData);
        onOpenChange(false);
        return;
      } catch (error) {
        console.error('Promote client to inquiry failed:', error);
        toast({ title: 'Failed to save', description: errorText(error), variant: 'destructive' });
        return;
      }
    }
    try {
      const result = await updateInquiry.mutateAsync({
        id: inquiry.id,
        updates: {
          // UX-2 — Save no longer re-sends projectStage. Status is the
          // only pipeline the user drives, and status changes persist
          // (with their derived stage) in handleStatusChange the moment
          // the dropdown moves. Re-sending the stale stage here could
          // only overwrite a finer-grained stage set from the project
          // board between opens.
          status: formData.status,
          clientPortalEnabled: formData.clientPortalEnabled,
          portalUrl: formData.portalUrl,
          estimatedCompletion: formData.estimatedCompletion,
          additionalNotes: formData.additionalNotes
        }
      });
      // HOTFIX-2 — the lead saved, but the stage/status push onto the
      // linked project may not have. That used to be dropped on the
      // floor entirely; now it gets its own honest toast.
      const syncError = (result as { projectSyncError?: string | null })?.projectSyncError;
      if (syncError) {
        toast({
          title: 'Lead saved — the linked project did not update',
          description: syncError,
          variant: 'destructive'
        });
      } else {
        toast({
          title: 'Changes saved',
          description: 'Inquiry updated successfully.'
        });
      }
      onUpdate(formData);
      onOpenChange(false);
    } catch (error) {
      console.error('Save inquiry failed:', error);
      toast({
        title: 'Error',
        description: `Failed to save changes — ${errorText(error)}`,
        variant: 'destructive'
      });
    }
  };

  const handleConvertToProject = async () => {
    try {
      const result = await convertToProject.mutateAsync(formData);
      toast({
        title: 'Project Created',
        description: 'Inquiry has been converted to a project successfully.'
      });
      onOpenChange(false);
      navigate(`/studio/${result.projectId}`);
    } catch (error) {
      // POL-16 — surface the real failure reason. The generic copy
      // hid a schema error for days; never swallow the message again.
      //
      // HOTFIX-2 — `error instanceof Error` was doing exactly the
      // swallowing POL-16 set out to stop: a PostgrestError is a plain
      // object, so the branch never fired and this rendered the bare
      // "Failed to convert inquiry to project." that the founder saw.
      console.error('Convert to project failed:', error);
      toast({
        title: 'Error',
        description: `Failed to convert inquiry to project — ${errorText(error)}`,
        variant: 'destructive'
      });
    }
  };

  const handleStatusChange = async (status: InquiryStatus) => {
    // UX-2 — derive the hidden stage from the chosen status. Stage is
    // no longer a dropdown here, but the projects table still speaks
    // it, so a status change carries a matching stage along invisibly
    // and useUpdateInquiry's existing sync pushes it to the linked
    // project. The `stageMatches` guard is load-bearing: when the
    // current stage already rolls up to this status (e.g. 'editing'
    // under In Progress), we leave the stage alone so a finer value
    // set from the project board never gets clobbered by a re-save.
    const currentStage = formData.projectStage || 'lead';
    const stageMatches = stageToInquiryStatus(currentStage) === status;
    const nextStage = stageMatches ? currentStage : statusToStage(status);
    const updatedData = { ...formData, status, projectStage: nextStage };
    setFormData(updatedData);

    // Auto-save status change to DB immediately.
    // DEMO-4 — this was console.error only. If the save failed, the
    // dropdown still showed the new status, nothing persisted, and
    // the studio was told nothing. Roll the local state back and say
    // so, so the UI never lies about what's on the server.
    if (!isClientDerived) {
      try {
        const result = await updateInquiry.mutateAsync({
          id: inquiry.id,
          updates: stageMatches ? { status } : { status, projectStage: nextStage },
        });
        const syncError = (result as { projectSyncError?: string | null })?.projectSyncError;
        if (syncError) {
          toast({
            title: 'Status saved — the linked project did not update',
            description: syncError,
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Failed to save status:', error);
        setFormData(formData);
        toast({
          title: "Status didn't save",
          description: `The lead is still "${formData.status}" — ${errorText(error)}`,
          variant: 'destructive'
        });
        return;
      }
    }

    // Auto-create project when status changes to accepted and no project exists
    if (!isClientDerived && status === 'accepted' && !formData.projectId) {
      try {
        const result = await convertToProject.mutateAsync(updatedData);
        toast({
          title: 'Project Created',
          description: 'Status changed to Accepted — project workspace created automatically.'
        });
        // Update formData with the new project ID
        setFormData((prev) => ({ ...prev, projectId: result.projectId }));
      } catch (error) {
        // DEMO-4 — was console.error only. The status flipped to
        // Accepted, no project appeared, and the studio had no idea
        // anything had gone wrong until they went looking for a
        // workspace that didn't exist. The status change itself DID
        // land (above), so we don't roll it back — we say exactly what
        // failed and point at the manual retry.
        //
        // UX-5 — the toast is no longer the only evidence: the lead
        // now sits in the derived accepted-without-project state, so
        // this modal shows a persistent "Project not created" banner
        // with a retry button, and the row in the leads table carries
        // a matching chip until a retry succeeds.
        console.error('Failed to auto-create project:', error);
        toast({
          title: 'Lead accepted — but the project wasn\'t created',
          description: `${errorText(error)} Use "Retry — create project" whenever you're ready; the lead is flagged until it works.`,
          variant: 'destructive'
        });
      }
    }
  };

  // UX-2 — handleStageChange is gone with the Project Stage dropdown.
  // Its stage→status derivation moved to services/inquiries.ts as
  // `stageToInquiryStatus`, and handleStatusChange above now runs the
  // mapping in the opposite direction.

  const handlePortalToggle = (enabled: boolean) => {
    setFormData({ ...formData, clientPortalEnabled: enabled });
  };

  const handleEmail = () => {
    window.open(`mailto:${inquiry.email}`);
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this inquiry?')) {
      onDelete(inquiry.id);
      onOpenChange(false);
    }
  };

  const handleClosedLost = () => {
    handleStatusChange('closed_lost');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canConvert = formData.status === 'accepted' && !formData.projectId;
  const hasLinkedProject = !!formData.projectId;
  // UX-5 — for a REAL inquiry, accepted-without-project means the
  // auto-create that runs on acceptance didn't finish: the status
  // flipped, the dependent project write failed, and the only
  // evidence used to be a toast that faded. Same derivation the row
  // chip in InquiryTable uses (`status='accepted' AND project_id IS
  // NULL`), so it survives reloads with no schema change. The banner
  // below renders as an explicit failure + retry in that case.
  // Client-derived rows are excluded — their 'accepted' status is
  // hardcoded by the dashboard merge, so nothing failed; they keep
  // the neutral "Ready to start production?" framing.
  const projectCreationIncomplete = canConvert && !isClientDerived;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-0">
          <div className="flex items-center gap-3">
            <DialogTitle>Inquiry Details</DialogTitle>
            {hasLinkedProject &&
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-primary/20 text-primary text-xs font-medium">
                <OrbitIcon name="link" fallback={Link} className="w-3 h-3" />
                Linked to Project
              </span>
            }
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Convert to Project CTA.
              UX-5 — two framings for the same derived state
              (accepted + no project_id):
              · real inquiry → the auto-create on acceptance did NOT
                finish, so this is a visible failure + retry that
                persists after the toast fades and across reloads;
              · client-derived row → nothing failed (its 'accepted' is
                hardcoded by the dashboard merge), keep the neutral
                invite. The retry is safe to hammer: the conversion is
                idempotent and adopts orphan projects via
                projects.inquiry_id. */}
          {canConvert && (projectCreationIncomplete ?
          <div className="bg-destructive/10 border border-destructive/40 rounded-lg p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-medium text-destructive flex items-center gap-2">
                    <OrbitIcon name="exclamationmark.triangle.fill" fallback={AlertTriangle} className="w-4 h-4" />
                    Project not created
                  </h3>
                  <p className="text-muted-foreground mt-1 text-xs">
                    This lead is accepted, but its project workspace was never created — the automatic
                    step didn't finish. Retry now; nothing will be duplicated.
                  </p>
                </div>
                <Button
                onClick={handleConvertToProject}
                disabled={convertToProject.isPending}
                variant="destructive"
                className="shrink-0">

                  {convertToProject.isPending ?
                <NovaSpinner size={16} className="mr-2" /> :

                <OrbitIcon name="arrow.clockwise" fallback={RefreshCw} className="w-4 h-4 mr-2" />
                }
                  Retry — create project
                </Button>
              </div>
            </div> :
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-foreground">Ready to start production?</h3>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Convert this inquiry into a full project with client portal, deliverables tracking, and more.
                  </p>
                </div>
                <Button
                onClick={handleConvertToProject}
                disabled={convertToProject.isPending}
                className="bg-primary hover:bg-primary/90 text-primary-foreground">

                  {convertToProject.isPending ?
                <NovaSpinner size={16} className="mr-2" /> :

                <OrbitIcon name="arrow.right" fallback={ArrowRight} className="w-4 h-4 mr-2" />
                }
                  Convert to Project
                </Button>
              </div>
            </div>)
          }

          {/* Linked Project Info */}
          {hasLinkedProject &&
          <div className="bg-primary/10 border border-primary/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-foreground flex items-center gap-2">
                    <OrbitIcon name="link" fallback={Link} className="w-4 h-4 text-primary" />
                    Project Workspace Ready
                  </h3>
                  <p className="text-muted-foreground mt-1 text-xs">
                    Manage deliverables, shot lists, tasks, and billing for this project.
                  </p>
                </div>
                <Button
                onClick={() => {
                  onOpenChange(false);
                  navigate(`/studio/${formData.projectId}`);
                }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground">

                  <OrbitIcon name="arrow.up.right.square" fallback={ExternalLink} className="w-4 h-4 mr-2" />
                  Open Workspace
                </Button>
              </div>
            </div>
          }

          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground text-sm">Name</Label>
              <p className="text-foreground font-medium mt-1">{inquiry.name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground text-sm">Email</Label>
              <p className="text-foreground font-medium mt-1">{inquiry.email}</p>
            </div>
            {inquiry.phone &&
            <div>
                <Label className="text-muted-foreground text-sm">Phone</Label>
                <p className="text-foreground font-medium mt-1">{inquiry.phone}</p>
              </div>
            }
            {inquiry.projectDate &&
            <div className="flex items-start gap-2">
                <OrbitIcon name="calendar" fallback={Calendar} className="w-4 h-4 text-muted-foreground mt-1" />
                <div>
                  <Label className="text-muted-foreground text-sm">Project Date</Label>
                  <p className="text-foreground font-medium mt-1">{inquiry.projectDate}</p>
                </div>
              </div>
            }
          </div>

          {/* Location & Package */}
          {(inquiry.location || inquiry.packageSelected) &&
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {inquiry.location &&
            <div className="flex items-start gap-2">
                  <OrbitIcon name="mappin.and.ellipse" fallback={MapPin} className="w-4 h-4 text-muted-foreground mt-1" />
                  <div>
                    <Label className="text-muted-foreground text-sm">Location</Label>
                    <p className="text-foreground font-medium mt-1">{inquiry.location}</p>
                  </div>
                </div>
            }
              {inquiry.packageSelected &&
            <div className="flex items-start gap-2">
                  <OrbitIcon name="shippingbox.fill" fallback={Package} className="w-4 h-4 text-muted-foreground mt-1" />
                  <div>
                    <Label className="text-muted-foreground text-sm">Package Selected</Label>
                    <p className="text-foreground font-medium mt-1">{inquiry.packageSelected}</p>
                  </div>
                </div>
            }
            </div>
          }

          {/* Project Description */}
          {inquiry.projectDescription &&
          <div className="flex items-start gap-2">
              <OrbitIcon name="doc.text" fallback={FileText} className="w-4 h-4 text-muted-foreground mt-1" />
              <div className="flex-1">
                <Label className="text-muted-foreground text-sm">Project Description</Label>
                <p className="text-foreground mt-1">{inquiry.projectDescription}</p>
              </div>
            </div>
          }

          {/* How they heard about us */}
          {inquiry.heardAboutUs &&
          <div className="flex items-start gap-2">
              <OrbitIcon name="megaphone.fill" fallback={Megaphone} className="w-4 h-4 text-muted-foreground mt-1" />
              <div>
                <Label className="text-muted-foreground text-sm">How they heard about us</Label>
                <p className="text-foreground font-medium mt-1">{inquiry.heardAboutUs}</p>
              </div>
            </div>
          }

          {/* Additional Notes */}
          {inquiry.additionalNotes &&
          <div className="flex items-start gap-2">
              <OrbitIcon name="text.bubble" fallback={MessageSquare} className="w-4 h-4 text-muted-foreground mt-1" />
              <div className="flex-1">
                <Label className="text-muted-foreground text-sm">Additional Notes</Label>
                <p className="text-foreground mt-1">{inquiry.additionalNotes}</p>
              </div>
            </div>
          }

          {/* Form Responses */}
          {inquiry.formResponses && inquiry.formResponses.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <OrbitIcon name="list.clipboard" fallback={ClipboardList} className="w-4 h-4 text-primary" />
                <Label className="text-foreground font-semibold text-sm">Intake Form Responses</Label>
              </div>
              <div className="bg-muted/50 border border-border rounded-lg divide-y divide-border">
                {inquiry.formResponses.map((resp, idx) => (
                  <div key={idx} className="p-3">
                    <p className="text-xs text-muted-foreground font-medium">{resp.question}</p>
                    <p className="text-sm text-foreground mt-0.5">
                      {Array.isArray(resp.answer) ? resp.answer.join(', ') : resp.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status — the one pipeline. UX-2 removed the Project Stage
              dropdown that used to sit beside this; stage lives on as a
              project concept (the /projects kanban) and is derived from
              status invisibly. */}
          <div>
            <Label className="text-muted-foreground text-sm mb-2 block">Status</Label>
            <Select
              value={formData.status || 'pending'}
              onValueChange={(v) => handleStatusChange(v as InquiryStatus)}>

              <SelectTrigger className="bg-secondary border-border">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border z-[9999]">
                {statusOptions.map((option) =>
                <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Client Portal */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <OrbitIcon name="link" fallback={Link} className="w-4 h-4 text-muted-foreground" />
                <Label className="text-foreground">Client Portal Enabled</Label>
              </div>
              <Switch
                checked={formData.clientPortalEnabled}
                onCheckedChange={handlePortalToggle} />

            </div>
            {formData.clientPortalEnabled && formData.portalUrl &&
            <div className="flex items-center gap-2">
                <Input
                placeholder="Portal URL"
                value={formData.portalUrl || ''}
                readOnly
                className="bg-secondary border-border" />

                <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(formData.portalUrl, '_blank')}>

                  <OrbitIcon name="arrow.up.right.square" fallback={ExternalLink} className="w-4 h-4" />
                </Button>
              </div>
            }
          </div>

          {/* Estimated Completion */}
          <div>
            <Label className="text-muted-foreground text-sm mb-2 block">Estimated Completion</Label>
            <Input
              type="date"
              value={formData.estimatedCompletion || ''}
              onChange={(e) => setFormData({ ...formData, estimatedCompletion: e.target.value })}
              className="bg-secondary border-border" />

          </div>

          {/* Submitted Timestamp */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <OrbitIcon name="clock" fallback={Clock} className="w-4 h-4" />
            <span className="text-sm">Submitted: {formatDate(inquiry.submittedAt)}</span>
          </div>

          {/* Automation Preview */}
          <div className="bg-secondary/50 border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-primary">
              <OrbitIcon name="bolt.fill" fallback={Zap} className="w-4 h-4" />
              <Label className="text-sm font-medium">Automation Preview</Label>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-status-accepted" />
                On status = Accepted → send 'Welcome / Next Steps' email
              </p>
              <p className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-status-follow-up" />
                On status = Follow Up → send reminder email in 3 days
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-card">
          <Button
            variant="ghost"
            onClick={handleClosedLost}
            className="text-status-follow-up hover:text-status-follow-up hover:bg-status-follow-up/10">

            <OrbitIcon name="xmark.circle.fill" fallback={XCircle} className="w-4 h-4 mr-2" />
            Closed Lost
          </Button>
          <div className="flex gap-3">
            <Button
              variant="ghost"
              onClick={handleDelete}
              className="text-destructive hover:text-destructive hover:bg-destructive/10">

              <OrbitIcon name="trash" fallback={Trash2} className="w-4 h-4 mr-2" />
              Delete
            </Button>
            <Button
              variant="outline"
              onClick={handleEmail}>

              <OrbitIcon name="envelope" fallback={Mail} className="w-4 h-4 mr-2" />
              Email
            </Button>
            <Button
              onClick={handleSave}
              disabled={updateInquiry.isPending}
              className="bg-primary hover:bg-primary/90 text-primary-foreground">

              {updateInquiry.isPending ?
              <NovaSpinner size={16} className="mr-2" /> :

              <OrbitIcon name="square.and.arrow.down" fallback={Save} className="w-4 h-4 mr-2" />
              }
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

};

// Wrapper component that handles null inquiry case
export const InquiryModal = ({ inquiry, open, onOpenChange, onUpdate, onDelete }: InquiryModalProps) => {
  if (!inquiry) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <div className="p-6 text-center text-muted-foreground">One sec — pulling their details.</div>
        </DialogContent>
      </Dialog>);

  }

  return (
    <InquiryModalContent
      inquiry={inquiry}
      open={open}
      onOpenChange={onOpenChange}
      onUpdate={onUpdate}
      onDelete={onDelete} />);

};